import React, { useEffect, useMemo, useState } from "react";
import { api, asset, IMG } from "@/lib/api";
import { PageHeader } from "@/components/shared/SectionHeader";
import { MapPin, ShieldCheck, Search } from "lucide-react";

const KINDS = ["All", "Breeder", "Veterinarian", "Sanctuary", "Rescue", "Behavior Consultant", "Nutrition Specialist", "Containment Professional"];

export default function Directory() {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/directory").then(r => setItems(r.data)); }, []);

  const filtered = useMemo(() => items.filter(d =>
    (kind === "All" || d.kind === kind) &&
    (q === "" || (d.name + " " + d.location + " " + d.specialty).toLowerCase().includes(q.toLowerCase()))
  ), [items, kind, q]);

  return (
    <div>
      <PageHeader
        eyebrow="Directory"
        title="Vetted breeders, veterinarians, sanctuaries and specialists."
        description="A verified network of professionals aligned with the Wolfdog Village standard."
        image={IMG.packSnow}
      />

      <section className="container-x py-14">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {KINDS.map(k => (
              <button key={k} onClick={() => setKind(k)} data-testid={`dir-kind-${k.toLowerCase().replace(/\s+/g,'-')}`}
                className={"text-xs uppercase tracking-[0.2em] font-semibold px-3 py-2 rounded-full border transition " + (kind === k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-foreground/40")}>{k}</button>
            ))}
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search directory…" data-testid="dir-search"
              className="w-full pl-9 pr-4 py-3 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(d => (
            <div key={d.id} data-testid={`dir-${d.id}`} className="group rounded-lg border border-border overflow-hidden bg-card hover:-translate-y-1 transition">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={asset(d.cover)} alt={d.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest font-semibold rounded-full bg-background/90 text-foreground px-2 py-1">{d.kind}</span>
                {d.verified && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-accent text-accent-foreground text-[10px] font-semibold px-2 py-1">
                    <ShieldCheck className="h-3 w-3" /> Verified
                  </span>
                )}
              </div>
              <div className="p-5">
                <div className="font-display text-xl">{d.name}</div>
                <div className="mt-1 text-xs text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {d.location}</div>
                <div className="mt-3 text-sm">{d.bio}</div>
                <div className="mt-3 eyebrow text-[10px]">{d.specialty}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
